import { LIBRARY_REFERENCE_ALIASES } from '@/data/library/library-reference-aliases';

export interface LibraryExerciseConfiguration {
  sets: number;
  reps: string;
  restSeconds: number;
}

/** Slots retain distinct execution/configuration identities while sharing one standard movement. */
export function getLibraryReferenceBaseId(id: string): string {
  return id.startsWith('library-') ? id.replace(/::slot-\d+-\d+$/, '') : id;
}

export function getLibraryReferenceIds(id: string): string[] {
  const base = getLibraryReferenceBaseId(id);
  return LIBRARY_REFERENCE_ALIASES[base] || [base];
}

/** A checkbox selection is a set. Training sequences below are deliberately not sets. */
export function normalizeLibrarySelectionIds(ids: string[]): string[] {
  return [...new Set(ids.filter(id => typeof id === 'string').flatMap(getLibraryReferenceIds))];
}

export function normalizeLibraryPlanReferences(
  ids: string[],
  configs?: Record<string, LibraryExerciseConfiguration>,
) {
  const exerciseIds: string[] = [];
  const exerciseConfigs: Record<string, LibraryExerciseConfiguration> = { ...configs };
  const used = new Set<string>();
  for (const [index, oldId] of ids.entries()) {
    for (const [part, base] of getLibraryReferenceIds(oldId).entries()) {
      const id = used.has(base) && base.startsWith('library-') ? `${base}::slot-${index}-${part}` : base;
      used.add(base);
      exerciseIds.push(id);
      const config = configs?.[oldId] || configs?.[getLibraryReferenceBaseId(oldId)];
      if (config) exerciseConfigs[id] = { ...config };
    }
  }
  return { exerciseIds, exerciseConfigs: configs ? exerciseConfigs : undefined };
}

/** Apply on read; a library deployment does not rewrite stored plans or historical workouts. */
export function normalizeLibraryCustomPlan<T extends {
  exerciseIds: string[];
  exerciseConfigs?: Record<string, LibraryExerciseConfiguration>;
}>(plan: T): T {
  return { ...plan, ...normalizeLibraryPlanReferences(plan.exerciseIds, plan.exerciseConfigs) };
}

export function normalizeFreeLibraryDay<T extends {
  exerciseIds?: string[];
  exerciseSettings?: Record<string, LibraryExerciseConfiguration>;
}>(day: T): T {
  const mapped = normalizeLibraryPlanReferences(day.exerciseIds || [], day.exerciseSettings);
  return { ...day, exerciseIds: mapped.exerciseIds, exerciseSettings: mapped.exerciseConfigs };
}
